import React from 'react';
import { StyleSheet, View } from 'react-native';

import { Chip } from '~/components/core';
import { useStyles } from '~/hooks';
import { scaleSize } from '~/constants/scale';
import { Theme } from '~/theme';

export type RecapEntry = {
    step: number;
    // Already-composed summary text; empty entries are skipped.
    text: string;
};

type Props = {
    entries: RecapEntry[];
    currentStep: number;
    onJump: (step: number) => void;
};

// Persistent context strip: one chip per completed earlier step, tappable to jump back —
// "what have I configured so far" without leaving the current step. Chips wrap onto further
// lines instead of scrolling sideways, so the whole recap is readable without interaction.
export default function RecapBar({ entries, currentStep, onJump }: Props) {
    const styles = useStyles(stylesGenerator);
    const visible = entries.filter((e) => e.text && e.step < currentStep);
    if (visible.length === 0) {
        return null;
    }
    return (
        <View style={styles.container}>
            {visible.map((e) => (
                <Chip
                    key={e.step}
                    label={e.text}
                    size="sm"
                    selected={false}
                    onPress={() => onJump(e.step)}
                    containerStyle={styles.chip}
                />
            ))}
        </View>
    );
}

const stylesGenerator = (_theme: Theme) =>
    StyleSheet.create({
        container: {
            flexDirection: 'row',
            flexWrap: 'wrap',
            gap: scaleSize(6),
            paddingHorizontal: scaleSize(12),
            marginBottom: scaleSize(6),
        },
        // A summary too long for a whole line shrinks and wraps its own text rather than
        // pushing the bar wider than the wizard column.
        chip: {
            flexShrink: 1,
        },
    });
